import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

interface Expense {
  id: string;
  title: string;
  amount: number;
  type: 'income' | 'expense';
  category: string;
  date: string;
  note?: string;
}

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    type: 'expense' as 'income' | 'expense',
    category: '',
    date: new Date().toISOString().split('T')[0],
    note: '',
  });

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    setIsLoading(true);
    const result = await apiClient.getNotes({ category: 'expense' });
    if (result.data) {
      // Parse notes as expenses
      const expenseData = result.data.notes.map((note: any) => ({
        id: note.id,
        title: note.title,
        amount: parseFloat(note.metadata?.amount || '0'),
        type: note.metadata?.type || 'expense',
        category: note.metadata?.category || '其他',
        date: note.created_at,
        note: note.content,
      }));
      setExpenses(expenseData);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const noteData = {
      title: formData.title,
      content: formData.note,
      category: 'expense',
      metadata: {
        amount: formData.amount,
        type: formData.type,
        category: formData.category,
        date: formData.date,
      },
    };

    const result = await apiClient.createNote(noteData);
    if (result.data) {
      loadExpenses();
      setShowForm(false);
      setFormData({
        title: '',
        amount: '',
        type: 'expense',
        category: '',
        date: new Date().toISOString().split('T')[0],
        note: '',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除这条记录吗？')) {
      const result = await apiClient.deleteNote(id);
      if (result.data) {
        setExpenses(expenses.filter(e => e.id !== id));
      }
    }
  };

  const totalIncome = expenses
    .filter(e => e.type === 'income')
    .reduce((sum, e) => sum + e.amount, 0);

  const totalExpense = expenses
    .filter(e => e.type === 'expense')
    .reduce((sum, e) => sum + e.amount, 0);

  const balance = totalIncome - totalExpense;

  return (
    <div className="expenses-page">
      <div className="expenses-header">
        <h2>💰 记账</h2>
        <button onClick={() => setShowForm(!showForm)} className="add-btn">
          {showForm ? '取消' : '+ 添加记录'}
        </button>
      </div>

      <div className="stats-cards">
        <div className="stat-card income">
          <div className="stat-label">收入</div>
          <div className="stat-value">¥{totalIncome.toFixed(2)}</div>
        </div>
        <div className="stat-card expense">
          <div className="stat-label">支出</div>
          <div className="stat-value">¥{totalExpense.toFixed(2)}</div>
        </div>
        <div className="stat-card balance">
          <div className="stat-label">结余</div>
          <div className="stat-value">¥{balance.toFixed(2)}</div>
        </div>
      </div>

      {showForm && (
        <div className="expense-form">
          <h3>添加记录</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>类型</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense' })}
                  required
                >
                  <option value="expense">支出</option>
                  <option value="income">收入</option>
                </select>
              </div>
              <div className="form-group">
                <label>金额</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>标题</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="例如：午餐、工资"
                  required
                />
              </div>
              <div className="form-group">
                <label>分类</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="例如：餐饮、交通"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>日期</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>备注</label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="可选"
                rows={3}
              />
            </div>

            <button type="submit" className="submit-btn">保存</button>
          </form>
        </div>
      )}

      <div className="expenses-list">
        {isLoading ? (
          <div className="loading">加载中...</div>
        ) : expenses.length === 0 ? (
          <div className="empty">
            <p>暂无记录</p>
            <button onClick={() => setShowForm(true)}>添加第一条记录</button>
          </div>
        ) : (
          expenses.map(expense => (
            <div key={expense.id} className={`expense-item ${expense.type}`}>
              <div className="expense-info">
                <div className="expense-title">{expense.title}</div>
                <div className="expense-meta">
                  <span className="expense-category">{expense.category}</span>
                  <span className="expense-date">
                    {new Date(expense.date).toLocaleDateString('zh-CN')}
                  </span>
                </div>
                {expense.note && <div className="expense-note">{expense.note}</div>}
              </div>
              <div className="expense-amount">
                {expense.type === 'income' ? '+' : '-'}¥{expense.amount.toFixed(2)}
              </div>
              <button
                className="delete-btn"
                onClick={() => handleDelete(expense.id)}
                title="删除"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      <style>{`
        .expenses-page {
          padding: 24px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .expenses-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .expenses-header h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: #333;
        }

        .add-btn {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .add-btn:hover {
          background: #0056b3;
        }

        .stats-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-card.income {
          border-left: 4px solid #4CAF50;
        }

        .stat-card.expense {
          border-left: 4px solid #F44336;
        }

        .stat-card.balance {
          border-left: 4px solid #2196F3;
        }

        .stat-label {
          font-size: 14px;
          color: #666;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: #333;
        }

        .expense-form {
          background: white;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .expense-form h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: #007bff;
        }

        .submit-btn {
          width: 100%;
          padding: 12px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .submit-btn:hover {
          background: #0056b3;
        }

        .expenses-list {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .expense-item {
          display: flex;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.2s;
        }

        .expense-item:hover {
          background: #f9f9f9;
        }

        .expense-item:last-child {
          border-bottom: none;
        }

        .expense-info {
          flex: 1;
        }

        .expense-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #333;
        }

        .expense-meta {
          display: flex;
          gap: 12px;
          font-size: 13px;
          color: #666;
        }

        .expense-category {
          padding: 2px 8px;
          background: #f0f0f0;
          border-radius: 4px;
        }

        .expense-note {
          margin-top: 8px;
          font-size: 13px;
          color: #666;
        }

        .expense-amount {
          font-size: 20px;
          font-weight: 700;
          margin: 0 16px;
        }

        .expense-item.income .expense-amount {
          color: #4CAF50;
        }

        .expense-item.expense .expense-amount {
          color: #F44336;
        }

        .delete-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .delete-btn:hover {
          background: #ffebee;
          color: #f44336;
        }

        .loading,
        .empty {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .empty p {
          font-size: 16px;
          margin-bottom: 16px;
        }

        .empty button {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .empty button:hover {
          background: #0056b3;
        }

        @media (max-width: 768px) {
          .expenses-page {
            padding: 16px;
          }

          .stats-cards {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
